import {AxiosInstance} from "axios";
import User from "../model/user/User";
import {RequestResetPasswordApiDto, UserCreateApiDto, UserUpdateApiDto} from "./model/rest";
import UserConsentData from "../model/user/UserConsentData";
import LocalStorage from "../store/local/LocalStorage";

/**
 * Api for user.
 */
export default class UserApi {
	constructor(public readonly axiosInstance: AxiosInstance) { }

	/**
	 * Log in user.
	 * @param username username.
	 * @param password password.
	 * @return {Promise<void>} promise with authorisation token.
	 */
	loginUser(username: string, password: string): Promise<void> {
		const url = `/api/web/session`;
		const data = {
			username: username,
			password: password
		};
		return this.axiosInstance.post(url, data).then(() => LocalStorage.clearWorkspaceFilters());
	}

	/**
	 * Logout user.
	 * @returns {Promise<void>} Promise of logging out user
	 */
	logoutUser() {
		const url = `/api/web/session`;
		return this.axiosInstance.delete(url).then(() => null);
	}

	/**
	 * Get user.
	 * @returns {Promise<string>} Promise with username.
	 */
	getUser(): Promise<User> {
		const url = `/api/web/users/me`;
		return this.axiosInstance.get(url).then(resp => User.fromJson(resp.data));
	}

	/**
	 * Get users.
	 * @return {Promise<User[]>} Promise with users.
	 */
	getUsers(): Promise<User[]> {
		const url = '/api/web/users';
		return this.axiosInstance.get(url).then(resp => resp.data.map(User.fromJson))
	}

	getUsersPage(params: {
		query?: string;
		role?: string | null;
		includeDisabled?: boolean;
		page?: number;
		size?: number;
		sort?: string;
	}): Promise<{ items: User[]; totalElements: number }> {
		const queryParams: Record<string, string> = {};
		if (params.query) queryParams.query = params.query;
		if (params.role) queryParams.role = params.role;
		if (params.includeDisabled !== undefined)
			queryParams.includeDisabled = String(params.includeDisabled);
		if (params.page !== undefined) queryParams.page = String(params.page);
		if (params.size !== undefined) queryParams.size = String(params.size);
		if (params.sort) queryParams.sort = params.sort;

		return this.axiosInstance
			.get('/api/web/users/page', {params: queryParams})
			.then(resp => ({items: resp.data.items.map(User.fromJson), totalElements: resp.data.totalElements}));
	}

	/**
	 * Create user.
	 * @param data User data.
	 * @return {Promise<User>}
	 */
	createUser(data: UserCreateApiDto): Promise<User> {
		const url = '/api/web/users';
		return this.axiosInstance.post(url, data).then(resp => User.fromJson(resp.data))
	}

	/**
	 * Update user
	 * @param {string} userId
	 * @param {UserUpdateApiDto} data
	 * @returns {Promise<User>}
	 */
	updateUser(userId: string, data: UserUpdateApiDto): Promise<User> {
		const url = `/api/web/users/${userId}`;
		return this.axiosInstance.put(url, data).then(resp => User.fromJson(resp.data))
	}

	/**
	 * Set user password.
	 * @param {string} password New password.
	 * @param {string} token Token from API.
	 */
	setPassword(password: string, token: string) {
		const url = '/api/web/users/password';
		const setPasswordData = {
			password: password,
			token: token
		};
		return this.axiosInstance.put(url, setPasswordData)
	}

	/**
	 * Request reset password.
	 * @param {RequestResetPasswordApiDto} data data for reset password request.
	 * @return {Promise<null>} empty promise.
	 */
	requestResetPassword(data: RequestResetPasswordApiDto): Promise<null> {
		const url = `/api/web/users/password/reset`;
		return this.axiosInstance.put(url, data).then(() => null);
	}

	/**
	 * Request reset password
	 * @param userId
	 */
	requestResetPasswordThroughSms(userId: string) {
		const url = `/api/web/users/${userId}/password/reset/sms`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Accept ToS
	 * @returns {Promise<null>}
	 */
	acceptTos(data: UserConsentData): Promise<null> {
		const url = `/api/web/users/consents`;
		return this.axiosInstance.put(url, data.toJson()).then(() => null);
	}

	/**
	 * Change user suspend
	 * @param {string} userId
	 * @param {boolean} disabled
	 * @returns {AxiosPromise<any>}
	 */
	changeSuspend(userId: string, disabled: boolean) {
		const url = `/api/web/users/${userId}/disabled`;
		return this.axiosInstance.put(url, {disabled})
	}
}